/*
 * HeroStudio — the landing hero's product-truth visual.
 *
 * Replaces a placeholder black rectangle. Renders the artifact
 * DemoCraft actually produces: a captioned walkthrough frame with
 * a click highlight, a scene strip, a narration waveform, and the
 * export formats. Built from CSS + inline SVG so it stays sharp at
 * any size, costs no image payload, and inherits design tokens.
 *
 * Accessibility: the whole composition is decorative — the hero's
 * <h1> and copy carry the message — so it is aria-hidden and
 * paired with a visually-hidden description for screen readers.
 * All motion is token-driven and stops under prefers-reduced-motion.
 */

const SCENES = [
  { n: "01", label: "Hook",     w: "18%" },
  { n: "02", label: "Problem",  w: "22%" },
  { n: "03", label: "Feature",  w: "26%", active: true },
  { n: "04", label: "Proof",    w: "20%" },
  { n: "05", label: "CTA",      w: "14%" },
];

/* A fixed, hand-tuned waveform. Deterministic beats Math.random():
   it must not reflow between renders or differ across SSR/hydration. */
const WAVE = [3,5,8,12,9,14,18,13,9,6,11,16,21,17,12,8,5,9,14,19,15,10,7,4,8,13,17,12,8,5,3];

export default function HeroStudio() {
  return (
    <figure className="m-0">
      <div
        className="brutal-card p-0 overflow-hidden bg-canvas"
        aria-hidden="true"
      >
        {/* ---- window chrome ---- */}
        <div className="flex items-center gap-3 px-4 h-10 border-b border-rule bg-surface">
          <div className="flex gap-1.5">
            <span className="w-2.5 h-2.5 border border-rule bg-brand" />
            <span className="w-2.5 h-2.5 border border-rule bg-state-warning" />
            <span className="w-2.5 h-2.5 border border-rule bg-state-success" />
          </div>
          <span className="font-mono text-2xs tracking-widest uppercase text-ink-muted truncate">
            launch-demo.mp4
          </span>
          <span className="ml-auto brutal-chip brutal-chip-success !text-2xs !py-0">
            Ready
          </span>
        </div>

        {/* ---- render stage (16:9) ---- */}
        <div className="relative aspect-video bg-surface-inverse overflow-hidden">
          {/* mock product UI being demoed */}
          <svg
            viewBox="0 0 320 180"
            className="absolute inset-0 w-full h-full"
            preserveAspectRatio="xMidYMid slice"
            focusable="false"
          >
            <defs>
              <linearGradient id="hs-vignette" x1="0" y1="0" x2="0" y2="1">
                {/* Held at 0 until below the primary button (y=122/180
                    = 68%) so the click target keeps its full #FF3B30.
                    The gradient exists only to seat the caption. */}
                <stop offset="0%"   stopColor="var(--surface-inverse)" stopOpacity="0" />
                <stop offset="68%"  stopColor="var(--surface-inverse)" stopOpacity="0" />
                <stop offset="100%" stopColor="var(--surface-inverse)" stopOpacity="0.8" />
              </linearGradient>
            </defs>

            {/* app frame */}
            <rect x="14" y="12" width="292" height="156" fill="var(--ink-900)" stroke="var(--ink-700)" />
            {/* sidebar */}
            <rect x="14" y="12" width="58" height="156" fill="var(--ink-850)" stroke="var(--ink-700)" />
            {[28, 42, 56, 70, 84].map((y, i) => (
              <rect key={y} x="22" y={y} width={i === 1 ? 34 : 42} height="5"
                    fill={i === 1 ? "var(--accent)" : "var(--ink-600)"} />
            ))}
            {/* toolbar */}
            <rect x="72" y="12" width="234" height="20" fill="var(--ink-850)" stroke="var(--ink-700)" />
            <rect x="82" y="19" width="46" height="6" fill="var(--ink-550)" />
            {/* content cards */}
            <rect x="82" y="44" width="98" height="44" fill="var(--ink-800)" stroke="var(--ink-700)" />
            <rect x="90" y="52" width="40" height="5" fill="var(--ink-550)" />
            <rect x="90" y="64" width="62" height="14" fill="var(--ink-750)" />
            <rect x="190" y="44" width="106" height="44" fill="var(--ink-800)" stroke="var(--ink-700)" />
            {/* mini bar chart */}
            {[10, 18, 13, 24, 20].map((h, i) => (
              <rect key={i} x={198 + i * 18} y={82 - h} width="11" height={h}
                    fill={i === 3 ? "var(--accent)" : "var(--ink-650)"} />
            ))}
            {/* the button being clicked */}
            <rect x="82" y="102" width="72" height="20" fill="var(--accent)" />
            <rect x="94" y="109" width="48" height="6" fill="var(--text-on-accent)" />
            <rect x="166" y="102" width="130" height="20" fill="var(--ink-800)" stroke="var(--ink-700)" />

            <rect x="0" y="0" width="320" height="180" fill="url(#hs-vignette)" />
          </svg>

          {/* click highlight — the product's signature overlay */}
          <span className="hs-click" />

          {/* burned-in caption, exactly as exported */}
          <div className="absolute left-0 right-0 bottom-3 flex justify-center px-4">
            <p className="font-mono text-xs text-ink-inverse bg-surface-inverse/85 border border-white/25 px-3 py-1.5 text-center leading-snug">
              “One click and your storyboard becomes a video.”
            </p>
          </div>

          <span className="absolute top-3 right-3 marquee-tag !text-2xs">Scene 03</span>
        </div>

        {/* ---- transport + narration waveform ---- */}
        <div className="flex items-center gap-3 px-4 h-11 border-t border-rule bg-canvas">
          <svg width="10" height="12" viewBox="0 0 10 12" className="shrink-0" focusable="false">
            <polygon points="0,0 0,12 10,6" fill="var(--text)" />
          </svg>
          <span className="font-mono text-2xs text-ink-muted tabular-nums shrink-0">00:14</span>
          <div className="flex-1 flex items-end gap-[2px] h-6 overflow-hidden" >
            {WAVE.map((h, i) => (
              <span
                key={i}
                className="flex-1 min-w-[2px]"
                style={{
                  height: `${(h / 21) * 100}%`,
                  background: i < 12 ? "var(--accent)" : "var(--border-muted)",
                }}
              />
            ))}
          </div>
          <span className="font-mono text-2xs text-ink-muted tabular-nums shrink-0">00:38</span>
        </div>

        {/* ---- scene strip ---- */}
        <div className="flex border-t border-rule">
          {SCENES.map((s) => (
            <div
              key={s.n}
              style={{ width: s.w }}
              className={`px-2 py-2.5 border-r border-rule last:border-r-0 min-w-0 ${
                s.active ? "bg-brand" : "bg-surface"
              }`}
            >
              <div className={`font-mono text-2xs font-bold ${s.active ? "text-ink-on-accent" : "text-ink"}`}>
                {s.n}
              </div>
              <div className={`font-mono text-xs truncate ${s.active ? "text-ink-on-accent" : "text-ink-subtle"}`}>
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ---- exports: real text, outside the artwork ---- */}
      <figcaption className="mt-4 flex flex-wrap items-center gap-2">
        <span className="eyebrow mr-1">Exports</span>
        {["MP4", "SRT", "PDF manual", "Launch kit"].map((f) => (
          <span key={f} className="brutal-chip">{f}</span>
        ))}
      </figcaption>

      <p className="sr-only">
        A DemoCraft project window titled launch-demo.mp4, marked ready. The
        render stage shows a product screen with a highlighted click on a
        primary button and a burned-in caption reading “One click and your
        storyboard becomes a video.” Below it, a narration waveform is part
        way through playback at fourteen seconds of thirty-eight, and a strip
        of five scenes — Hook, Problem, Feature, Proof, and CTA — shows scene
        three selected. The project exports as MP4, SRT, PDF manual, and
        launch kit.
      </p>
    </figure>
  );
}
